/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-lone-blocks */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  onValidateLoginTokenReset,
  onValidateLoginTokenSubmit,
} from "../../Store/Slices/validateLoginTokenSlice";
import {
  onGetMember,
  onGetMemberReset,
} from "../../Store/Slices/getMemberSlice";
import { onSavePoints } from "../../Store/Slices/cardDataSlice";
import Loader from "../../Components/Loader/Loader";
import PageError from "../../Components/PageError/PageError";
import { ClientJourneyType, trackEvent } from "../../Components/Utility/Utility";
import { callActiveCardPostApi } from "../../Context/activeCardApi";
import {
  onPostAddToCart,
  onPostAddToCartReset,
} from "../../Store/Slices/addToCartSlice";
import getTranslationData from "../../TranslationUtility/TranslationUtility";

// SSO security migration (GIFT-3862): this page intentionally no longer imports
// generateTokenSlice, axiosIntegration/axiosIntegrationOtp, onMemberPost, API, or
// the V3 credential state. Client API now generates the integration access token,
// validates SSO, and synchronizes the member/cards. Do not restore those browser
// credential or duplicate member-update calls; usePoints was also unused here.

const SsoLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translation=getTranslationData();
  const clientJourneyType = ClientJourneyType();
  const [accountData, setAccountData] = useState([]);
  const [isApiCallsDone, setIsApiCallsDone] = useState(false);
  const [isPendingAddToCart, setIsPendingAddToCart] = useState(false);
  const hasHandledNavigation = useRef(false); // Prevent multiple redirects
  const hasCalledValidateToken = useRef(false); // Prevent multiple validateLoginToken calls
  const addToCartResponse = useSelector((state) => state.addToCartReducer);
  const loginToken = useSelector(
    (state) => state.validateLoginTokenSliceReducer
  );
  const memberData = useSelector((state) => state.getMemberSliceReducer);
  const cards =
    memberData?.MemberData?.otherListAttribute?.[0]?.valueDetails?.filter(
      (cardData) => cardData.status !== "0"
    );

  const loginTokenData = loginToken.data;
  const urlIndex = location.pathname.split("/");

  useEffect(() => {
    sessionStorage.removeItem("login");
  }, []);

  useEffect(() => {
    sessionStorage.setItem("loginSessionToken", urlIndex[2]);
    sessionStorage.setItem("temporaryToken", urlIndex[3]);
    if (!sessionStorage.getItem("login") && !hasCalledValidateToken.current) {
      hasCalledValidateToken.current = true;
      dispatch(
        onValidateLoginTokenSubmit({
          loginSessionToken: urlIndex[2],
          temporaryToken: urlIndex[3],
          urlReferrer: "",
        })
      );
    }
  }, []);
  useEffect(() => {
    if (loginToken.status_code === "200") {
      sessionStorage.setItem("login", true);
      sessionStorage.removeItem("temporaryToken");
      if (loginToken.data?.isNewMember === false) {
        trackEvent("login", {
          method: "sso",
          first_name: loginTokenData?.memberProfile?.firstName || "F",
          last_name: loginTokenData?.memberProfile?.lastName || "L",
          email: loginTokenData?.memberProfile?.emailId || "",
          phone: loginTokenData?.memberProfile?.mobileNumber || "",
          Xid: Cookies.get('xid') || "",
        });
      }
      else {
        trackEvent("sign_up", {
          method: "sso",
          first_name: loginTokenData?.memberProfile?.firstName || "F",
          last_name: loginTokenData?.memberProfile?.lastName || "L",
          email: loginTokenData?.memberProfile?.emailId || "",
          phone: loginTokenData?.memberProfile?.mobileNumber || "",
          Xid: Cookies.get('xid') || "",
        });
      }

      dispatch(onGetMember(loginToken));
      const pendingPayload = sessionStorage.getItem("pendingAddToCartPayload");

      if (pendingPayload) {
        try {
          const parsedPayload = JSON.parse(pendingPayload);
          parsedPayload.memberId = loginTokenData?.memberProfile?.mobileNumber;
          setIsPendingAddToCart(true);
          dispatch(onPostAddToCart(parsedPayload));
        } catch (error) {
          console.error("Invalid pending Add To Cart payload:", error);
          setIsPendingAddToCart(false);
        }
      }
      dispatch(onValidateLoginTokenReset());
    }
  }, [loginToken]);

  useEffect(() => {
    if (memberData?.get_status_code === "200" && memberData?.MemberData) {
      if (clientJourneyType === true) {
        callActiveCardPostApi({
          identifier: memberData?.MemberData.memberIdentifier,
          subIdentifier: memberData?.MemberData.subIdentifier,
          identifierType:
            memberData?.MemberData?.subIdentifierType === null
              ? 1001
              : memberData?.MemberData?.subIdentifierType,
        }).then((res) => {
          setAccountData((prevData) => [...prevData, res]);
          dispatch(
            onSavePoints({
              pointBalance:
                res?.response?.memberBalance?.pointBalance ??
                memberData?.MemberData?.memberBalance?.[0]?.pointBalance,
              identifierType: creditCards?.[0]?.identifierType,
              subIdentifier: creditCards?.[0]?.subIdentifier,
            }),
          );
        });
        if (!isPendingAddToCart) {
          setIsApiCallsDone(true);
        }
        dispatch(onGetMemberReset());
        return;
      }
      if (!isPendingAddToCart) {
        setIsApiCallsDone(true);
      }
      dispatch(onGetMemberReset());
    }
  }, [clientJourneyType, memberData]);

  const mergedArray = useMemo(
    () =>
      accountData
        .map((cardData) => cardData.response)
        ?.map((item1) => {
          const matchingItem = cards?.find(
            (item2) => item2.identifier === item1.subIdentifier
          );
          return {
            ...item1,
            ...(matchingItem ? { cardDetails: matchingItem } : {}),
          };
        }),
    [cards]
  );

  const creditCards = useMemo(
    () =>
      mergedArray?.filter(
        (card) => card?.cardDetails?.identifiertype === "1002"
      ),
    [mergedArray]
  );

  useEffect(() => {
    if (
      isApiCallsDone &&
      !isPendingAddToCart &&
      !hasHandledNavigation.current
    ) {
      hasHandledNavigation.current = true;
      const arrayData = sessionStorage.getItem("arrayData");
      let redirectPath = null;

      if (loginTokenData?.redirectPath) {
        try {
          const decodedPath = atob(loginTokenData.redirectPath);
          const url = new URL(decodedPath, window.location.origin);
          const targetPath = url.pathname || "/";
          const param1Value = url.searchParams.get("Param1");
          const normalizeEncodedParam1 = (value) => {
            const trimmedValue = value.trim();
            const separatorIndex = trimmedValue.indexOf("=");

            if (separatorIndex === -1) {
              try {
                return encodeURIComponent(decodeURIComponent(trimmedValue));
              } catch {
                return encodeURIComponent(trimmedValue);
              }
            }

            const rawKey = trimmedValue.slice(0, separatorIndex);
            const rawValue = trimmedValue.slice(separatorIndex + 1);
            const normalizedKey = decodeURIComponent(rawKey);

            try {
              const decodedValue = decodeURIComponent(rawValue);
              return `${normalizedKey}=${encodeURIComponent(decodedValue)}`;
            } catch {
              return `${normalizedKey}=${encodeURIComponent(rawValue)}`;
            }
          };

          if (param1Value && param1Value.trim() !== "") {
            redirectPath = `${targetPath}?${normalizeEncodedParam1(param1Value)}`;
          } else {
            // Handle multiple query parameters from decoded path
            const queryParams = new URLSearchParams();
            url.searchParams.forEach((value, key) => {
              if (key !== "Param1") {
                queryParams.set(key, value);
              }
            });
            const queryString = queryParams.toString();
            redirectPath = queryString
              ? `${targetPath}?${queryString}`
              : targetPath;
          }
        } catch (error) {
          console.error("Failed to decode redirectPath:", error);
        }
      }

      if (redirectPath) {
        if (arrayData) {
          const parsedArrayData = JSON.parse(arrayData);
          navigate(redirectPath, {
            state: { arrayData: parsedArrayData },
            replace: true,
          });
          sessionStorage.removeItem("arrayData");
        } else {
          navigate(redirectPath, { replace: true });
        }
      } else if (localStorage.getItem("ProductListPage")) {
        if (localStorage.getItem("ProductListPage") === "/order-detail") {
          navigate("/orders", { replace: true });
        } else {
          if (arrayData) {
            const parsedArrayData = JSON.parse(arrayData);
            navigate(localStorage.getItem("ProductListPage"), {
              state: { arrayData: parsedArrayData },
              replace: true,
            });
            sessionStorage.removeItem("arrayData");
          } else {
            navigate(localStorage.getItem("ProductListPage"), {
              replace: true
            });
          }
        }
        localStorage.removeItem("ProductListPage");
      } else {
        if (arrayData) {
          sessionStorage.removeItem("arrayData");
        }
        navigate("/", { replace: true });
      }

      sessionStorage.setItem(
        "accountData",
        JSON.stringify(accountData.map((cardData) => cardData.response))
      );
      {
        cards && sessionStorage.setItem("cardInfo", JSON.stringify(cards));
      }
    }
  }, [isApiCallsDone, isPendingAddToCart, navigate, loginTokenData]);

  useEffect(() => {
    if (!isPendingAddToCart) {
      return;
    }

    if (addToCartResponse?.post_status_code === "200") {
      sessionStorage.removeItem("pendingAddToCartPayload");
      sessionStorage.removeItem("pendingAddToCartReturnPath");
      setIsPendingAddToCart(false);
      setIsApiCallsDone(true);
      dispatch(onPostAddToCartReset());
      return;
    }

    if (["400", "401", "500"].includes(addToCartResponse?.post_status_code)) {
      const message =
        addToCartResponse?.postMessage || translation.unableAddToCart;
      sessionStorage.setItem("addToCartError", message);
      const returnPath = sessionStorage.getItem("pendingAddToCartReturnPath");

      sessionStorage.removeItem("pendingAddToCartPayload");
      sessionStorage.removeItem("pendingAddToCartReturnPath");

      setIsPendingAddToCart(false);
      setIsApiCallsDone(false);
      hasHandledNavigation.current = true;

      dispatch(onPostAddToCartReset());

      if (returnPath) {
        navigate(returnPath, {
          replace: true,
          state: {
            addToCartError: message,
          },
        });
      }
    }
  }, [addToCartResponse?.post_status_code, isPendingAddToCart]);

  if (loginToken.isError) {
    return (
      <PageError
        pageError={{
          StatusCode: String(loginToken.status_code || "401"),
          ErrorName: "Unable to sign in",
          ErrorDescription:
            loginToken.message ||
            "Your login session is invalid or expired. Please start sign-in again.",
          url: "/",
          buttonText: "Back to home",
        }}
      />
    );
  }

  return (
    <>
      <Loader />
    </>
  );
};

export default SsoLogin;
